<template>
	<div
		v-if="message"
		:class="outerAnchorClass"
		class="c-Tooltip"
		@mouseover="calculateDisplayPosition()"
		@focus="calculateDisplayPosition()"
		@mouseleave="hideTooltip()"
		@blur="hideTooltip()">
		<div
			ref="tooltipIcon"
			class="c-Tooltip__icon">
			<i v-if="anchorName === 'self'"></i>
		</div>
		<div
			v-show="tooltipVisible"
			ref="tooltipMessageContainer"
			class="c-Tooltip__messageContainer"
			:class="cssClassesMessagePosition">
			<p class="c-Tooltip__message">{{ message }}</p>
		</div>
	</div>
</template>

<script>
export default {
	name: 'Tooltip',
	props: {
		/**
     * The message to be displayed on the Tooltip
     */
		message: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The Tooltip's display position in realtion with it's trigger
		 * @values top, right, bottom, left
     */
		position: {
			type: String,
			required: false,
			default: () => 'top',
			validator (pos) {
				return [
					'top',
					'right',
					'bottom',
					'left'
				].includes(pos)
			}
		},
		/**
     * ID of the element which will trigger the Tooltip
     */
		anchorName: {
			type: String,
			required: false,
			default: () => 'self'
		}
	},
	data () {
		return {
			tooltipVisible: false,
			currPosition: 'top'
		}
	},
	computed: {
		outerAnchorClass() {
			if (this.anchorName !== 'self') {
				return 'outer-anchor'
			}

			return ''
		},
		cssClassesMessagePosition() {
			const positions = Object.freeze({
				top: 'top-position',
				right: 'right-position',
				bottom: 'bottom-position',
				left: 'left-position'
			})

			if (positions[this.currPosition]) {
				return positions[this.currPosition]
			}

			return false
		}
	},
	watch: {
		position: {
			handler (newVal) {
				this.currPosition = newVal
			},
			immediate: true
		}
	},
	mounted() {
		if (this.anchorName !== 'self') {
			const anchorElem = document.getElementById(this.anchorName)
			// set some mandatory styling for the parent element
			anchorElem.style.position = 'relative'

			// set layer over anchorElement
			const icon = this.$refs.tooltipIcon
			icon.style.height = `${anchorElem.offsetHeight}px`
			icon.style.width = `${anchorElem.offsetWidth}px`
		}
	},
	methods: {
		hideTooltip () {
			this.tooltipVisible = false
		},
		calculateDisplayPosition() {
			// Destructure objects from $refs
			const {
				tooltipMessageContainer
			} = this.$refs

			// Get tooltip dimensions
			const tooltipMessageWidth = tooltipMessageContainer.clientWidth
			const tooltipMessageHeight = tooltipMessageContainer.clientHeight

			// Get Viewport dimensions
			const viewportWidth = window.innerWidth
			const viewportHeight = window.innerHeight

			this.setDisplayPosition()

			// Check overflows
			const overflows = {
				top: tooltipMessageContainer.getBoundingClientRect().y < 0 || false,
				right:
          tooltipMessageContainer.getBoundingClientRect().x + tooltipMessageWidth
            > viewportWidth || false,
				bottom:
          tooltipMessageContainer.getBoundingClientRect().y + tooltipMessageHeight
						> viewportHeight || false,
				left: tooltipMessageContainer.getBoundingClientRect().x < 0 || false
			}

			if (this.position === 'left' && overflows.left && !overflows.right) {
				this.currPosition = 'right'
			} else if (this.position === 'right' && !overflows.left && overflows.right) {
				this.currPosition = 'left'
			}	else if (this.position === 'top' && overflows.top && !overflows.bottom) {
				this.currPosition = 'bottom'
			} else if (this.position === 'bottom' && !overflows.top && overflows.bottom) {
				this.currPosition = 'top'
			}

			this.setDisplayPosition()
			this.tooltipVisible = true
		},
		setDisplayPosition () {
			const baseFontSize = 16

			// Destructure objects from $refs
			const {
				tooltipMessageContainer,
				tooltipIcon
			} = this.$refs

			const iconWidth = tooltipIcon.offsetWidth
			const iconHeight = tooltipIcon.offsetHeight

			// Get tooltip dimensions
			const tooltipMessageWidth = tooltipMessageContainer.clientWidth
			const tooltipMessageHeight = tooltipMessageContainer.clientHeight

			// Set tooltip positions
			if (this.anchorName === 'self') {
				if (this.currPosition === 'top') {
					tooltipMessageContainer.style.left = '0px'
					tooltipMessageContainer.style.top = `${-tooltipMessageHeight - baseFontSize / 2}px`
				} else if (this.currPosition === 'right') {
					tooltipMessageContainer.style.top = '-1px'
					tooltipMessageContainer.style.left = `${iconWidth + baseFontSize / 2}px`
				} else if (this.currPosition === 'bottom') {
					tooltipMessageContainer.style.left = '0px'
					tooltipMessageContainer.style.top = `${iconHeight + baseFontSize / 2}px`
				} else if (this.currPosition === 'left') {
					tooltipMessageContainer.style.top = '-1px'
					tooltipMessageContainer.style.left = `-${tooltipMessageWidth + baseFontSize / 2}px`
				}
			} else if (this.anchorName !== 'self') {
				const anchorElem = document.getElementById(this.anchorName)
				const anchorWidth = anchorElem.offsetWidth
				const anchorHeight = anchorElem.offsetHeight

				if (this.currPosition === 'top') {
					tooltipMessageContainer.style.left = `${anchorWidth / 2}px`
					tooltipMessageContainer.style.top = `${- anchorHeight - baseFontSize / 2}px`
				} else if (this.currPosition === 'right') {
					tooltipMessageContainer.style.top = '0px'
					tooltipMessageContainer.style.left = `${anchorWidth + baseFontSize / 2}px`
				} else if (this.currPosition === 'bottom') {
					tooltipMessageContainer.style.left = `${anchorWidth / 2}px`
					tooltipMessageContainer.style.top = `${anchorHeight + baseFontSize / 2}px`
				} else if (this.currPosition === 'left') {
					tooltipMessageContainer.style.top = '0px'
					tooltipMessageContainer.style.left = `-${tooltipMessageWidth + baseFontSize / 2}px`
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';

.c-Tooltip {
	position: relative;

	&.outer-anchor {
		height: inherit;
		width: inherit;
		position: absolute;
		top: 0;
		left: 0;

		.c-Tooltip__messageContainer.top-position, .c-Tooltip__messageContainer.bottom-position {
			transform: translateX(-50%);
		}
	}

  &__icon {
		height: inherit;
		display: inline-block;

    span {
      height: inherit;
		}

		i {
			width: 24px;
			height: 24px;
			display: block;
			background-size: cover;
			background-repeat: no-repeat;
			background-position: center;
			background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10S2 17.514 2 12 6.486 2 12 2zm0 1.25c-4.825 0-8.75 3.925-8.75 8.75s3.925 8.75 8.75 8.75 8.75-3.925 8.75-8.75S16.825 3.25 12 3.25zm-.625 6.875c.69 0 1.25.56 1.25 1.25V14.5c0 .345.28.625.625.625h.625a.625.625 0 010 1.25h-.625a1.877 1.877 0 01-1.875-1.875v-3.125h-.625a.625.625 0 010-1.25h.625zM11.688 7a.937.937 0 110 1.875.937.937 0 010-1.875z' fill='%23343941' fill-rule='evenodd'/%3E%3C/svg%3E");
		}
  }

  &__messageContainer {
		position: absolute;

		&.top-position, &.bottom-position {
			transform: translateX(calc(-50% + 12px));
		}
	}

	&__message {
		background-color: $un-n900;
		border-radius: $border-radius;
		padding: calc(#{$base-padding} / 4) calc(#{$base-padding} / 2);
		text-align: center;
		font-family: $primary-font;
		font-size: 12px;
		line-height: 18px;
		color: $un-white;
		white-space: nowrap;
		max-width: 320px;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}
</style>
